/**
 * Turn a batch of trip events into short, human lines grouped by list — the
 * preview under "Apply changes" and the "Recently applied" list. Several events
 * on one item by one person collapse into one line ("renamed and checked
 * “Snorkel”"). Pure; unit-tested.
 */
import { type TripItemEvent, tripListKey } from '@ultimate-checklist/shared';

export interface ChangeLine {
  key: string;
  listKey: string;
  listName: string;
  /** Null for list-level events (the whole list was removed, etc.). */
  itemId: string | null;
  text: string;
}

export interface ChangeGroup {
  listKey: string;
  listName: string;
  lines: ChangeLine[];
}

type Verb = { verb: string; suffix?: string; listLevel?: boolean };

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

/** What someone did, phrased to read as "<who> <verb> “<item>”<suffix>". */
function verbFor(event: TripItemEvent): Verb {
  const data = event.data ?? {};
  const str = (key: string) => (typeof data[key] === 'string' ? (data[key] as string) : '');
  const fieldAfter = (key: string) => {
    const change = asRecord(asRecord(data.fields)?.[key]);
    return typeof change?.after === 'string' ? change.after : '';
  };
  switch (event.type) {
    case 'created':
      return { verb: 'added' };
    case 'status_changed': {
      const to = str('to') || fieldAfter('status');
      return { verb: to === 'done' ? 'checked' : to === 'doing' ? 'started' : 'unchecked' };
    }
    case 'renamed':
      return { verb: 'renamed' };
    case 'note_changed':
      return { verb: 'edited' };
    case 'for_person_changed':
      return { verb: 'changed who it’s for:' };
    case 'assigned':
      return { verb: 'took on' };
    case 'unassigned':
      return { verb: 'unassigned' };
    case 'label_added':
      return { verb: 'labelled', suffix: str('label') ? ` “${str('label')}”` : '' };
    case 'label_removed':
      return { verb: 'removed a label from' };
    case 'question_opened':
      return { verb: 'asked about' };
    case 'question_resolved':
      return { verb: 'answered the question on' };
    case 'bought':
      return { verb: 'bought' };
    case 'comment_added':
      return { verb: 'commented on' };
    case 'comment_edited':
      return { verb: 'edited a comment on' };
    case 'comment_deleted':
      return { verb: 'deleted a comment on' };
    case 'flagged':
      return { verb: 'flagged' };
    case 'unflagged':
      return { verb: 'unflagged' };
    case 'lesson_created':
      return { verb: 'captured a lesson on' };
    case 'item_linked':
      return { verb: 'linked' };
    case 'item_unlinked':
      return { verb: 'unlinked' };
    case 'deleted':
      return { verb: 'removed' };
    case 'restored':
      return { verb: 'restored' };
    case 'moved': {
      const to = asRecord(data.to);
      const toName = typeof to?.originMasterListName === 'string' ? to.originMasterListName : '';
      return { verb: 'moved', suffix: ` to “${toName || 'Manually added items'}”` };
    }
    case 'list_promoted':
      return { verb: 'pushed the list to the Library', listLevel: true };
    case 'list_removed':
      return { verb: 'removed the whole list', listLevel: true };
    case 'list_renamed':
      return { verb: `renamed the list to “${str('to')}”`, listLevel: true };
    default:
      return { verb: 'changed' };
  }
}

function joinVerbs(verbs: string[]): string {
  if (verbs.length <= 1) {
    return verbs[0] ?? '';
  }
  return `${verbs.slice(0, -1).join(', ')} and ${verbs.at(-1)}`;
}

export function summarizeChanges(events: TripItemEvent[]): ChangeGroup[] {
  // Oldest first so combined verbs read in the order they happened.
  const ordered = [...events].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const groups = new Map<string, ChangeGroup>();
  const lines = new Map<
    string,
    ChangeLine & { verbs: string[]; suffixes: string[]; who: string; listLevel: boolean }
  >();
  // The newest event for an item carries its current name.
  const nameFor = new Map<string, string>();
  for (const event of ordered) {
    if (event.tripItemId && event.tripItemName) {
      nameFor.set(event.tripItemId, event.tripItemName);
    }
  }

  for (const event of ordered) {
    const listKey = tripListKey(event);
    const listName = event.originMasterListName ?? 'Manually added items';
    const group = groups.get(listKey) ?? { listKey, listName, lines: [] };
    groups.set(listKey, group);

    const { verb, suffix, listLevel } = verbFor(event);
    const lineKey = `${event.tripItemId ?? event.id}:${event.actorId ?? ''}`;
    const existing = lines.get(lineKey);
    if (existing) {
      if (!existing.verbs.includes(verb)) {
        existing.verbs.push(verb);
      }
      if (suffix) {
        existing.suffixes.push(suffix);
      }
      continue;
    }
    const line = {
      key: lineKey,
      listKey,
      listName,
      itemId: event.tripItemId,
      text: '',
      verbs: [verb],
      suffixes: suffix ? [suffix] : [],
      who: event.actorName?.trim() || 'Someone',
      listLevel: Boolean(listLevel),
    };
    lines.set(lineKey, line);
    group.lines.push(line);
  }

  for (const line of lines.values()) {
    const name = line.itemId ? nameFor.get(line.itemId) : undefined;
    const subject = line.listLevel ? '' : name ? ` “${name}”` : ' an item';
    line.text = `${line.who} ${joinVerbs(line.verbs)}${subject}${line.suffixes.join('')}`;
  }
  return [...groups.values()];
}

export function countLines(groups: ChangeGroup[]): number {
  return groups.reduce((total, group) => total + group.lines.length, 0);
}
